import { Actions } from '../actions';
import { Patient as PxPatient } from '../types/prescription';
import {
    CLEAR_PRESCRIPTION_PAD,
    DELETE_ADVICE,
    DELETE_ADVICE_PERMANENTLY,
    EDIT_ADVICE,
    DELETE_LAB_VITAL,
    DELETE_MEDICAL_HISTORY,
    CLEAR_MEDICAL_HISTORY,
    LOAD_PRESCRIPTION,
    UPDATE_EXAMINATION,
    UPDATE_EXAMINATION_FINDINGS,
    UPDATE_FOLLOWUP,
    UPDATE_PP_NOTES,
    UPDATE_REFERAL,
    UPDATE_VITALS,
    UPSERT_ADVICE,
    UPSERT_DYFORM,
    UPSERT_LAB_VITAL,
    UPSERT_MEDICAL_HISTORY,
    UPDATE_VITALS_V0,
    LOAD_PRESCRIPTION_TEMPLATE,
    UPDATE_PP_PRIVATE_NOTES,
    DOWNLOAD_PT_DIR,
    UPSERT_ADMINISTERED_VACCINES,
    UPSERT_MILESTONES,
    CLEAR_ADMINISTERED_VACCINES,
    SET_MILESTONES,
    UPDATE_MILESTONE,
} from '../actions/prescriptionPad';
import {
    LOAD_PAD_DY_FORM,
    LOAD_VITALS_CONFIG,
    SETUP_SETTINGS,
    SET_EDIT_PATIENT,
    SET_PREVIEW_DIRTY,
    TOGGLE_GOOGLE_REVIEW_DIALOG,
    TOGGLE_IFRAME_DIALOG,
    TOGGLE_PRIVACY_DIALOG,
    UPDATE_GENIE_SETTINGS,
    UPSERT_GENIE_SETTINGS,
    UPSERT_VITALS_CONFIG,
    UPSERT_CALCULATOR_CONFIG,
    LOAD_CALCULATOR_CONFIG,
    REFRESH_ALL_PATIENTS,
    TOGGLE_GLOBAL_SEARCH,
    SET_KHATA_CREDITS,
    TOGGLE_STORAGE_MODAL,
    SET_EXTRA_FLAGS,
    SET_PINCODE,
    TOGGLE_SHOW_EKA_COMM,
    LOAD_SECTION_NAME_CONFIG,
    UPSERT_SECTION_NAME_CONFIG,
    SET_ACTIVE_ORDER_UPDATES_COUNT,
    UPDATE_CLAIMABLE_AMOUNT,
    SET_DONT_ALLOW_START_VISIT,
    SET_IS_NOTES_MODE,
    SET_IS_TREATMENT_PLAN_ENABLED,
    SET_IS_DICOM_ENABLED,
    SET_CARE_CANVAS_PREVIEW,
    TOGGLE_LOGOUT_MODAL,
    SET_BUSINESS_UHID_PREFIX,
    SET_GLOBAL_SEARCH_FLAG,
} from '../actions/session';
import { Patient } from '../types/patient';
import { UPDATE_GENIE_PROFILE } from '../actions/login';
import {
    CalculatorsConfig,
    GenieSettings,
    IClaimableAmount,
    VitalsConfig,
} from '../../api/pushGenie';
import {
    DEFAULT_CALCULATOR_CONFIG_VALUES,
    DEFAULT_GENIE_SETTINGS,
    DEFAULT_SECTION_NAME_CONFIG_VALUES,
    DEFAULT_VITALS_CONFIG_VALUES,
} from '../../constants';
import { getMedElementsMergedWithDefault, getPadElementMergedWithDefault } from '../utils';
import { GDyForm, SectionNameConfig } from '../db';
import { createAllPtsExcelAndDownload } from '../../pages/Settings';
import { notification } from 'antd';
import { MHC } from '../types/medicalHistory';
import { StorageInfoResponse } from '../../api/fetchStorageInfo';
import { CareCanvas } from '../types/CareCanvas';

export type PadConfig = {
    vitalsPadConfig?: VitalsConfig;
    calculatorPadConfig?: CalculatorsConfig;
    sectionNameConfig?: SectionNameConfig;
    dyForm?: GDyForm[];
};

export interface SessionReducerV1 extends GenieSettings {
    isPreviewDirty: boolean;
    selectedPatient?: PxPatient;
    privacyDialogVisible: boolean;
    googleReviewDialogVisible?: string;
    showEkaCommunications: boolean;
    iframeDialogUrl?: string;
    padConfig?: PadConfig;
    editPatient?: Patient;
    refreshPage?: boolean;
    isGlobalSearchVisible: boolean;
    isStorageModalVisible: boolean;
    isSettingUp: boolean;
    khataCredits?: number;
    ptMedicalHistory?: MHC;
    abhaPotentialLoss?: number;
    currentMonthLoss?: number;
    storageInfo?: StorageInfoResponse;
    pincode?: string;
    activeOrderUpdatesCount?: number;
    claimableAmount?: IClaimableAmount[];
    isNotesMode?: boolean | undefined;
    dontAllowStartVisit?: boolean | undefined;
    isTreatmentPlanEnabled?: boolean;
    isDicomEnabled?: boolean;
    careCanvasPreview?: CareCanvas;
    showLogoutModal?: boolean;
    businessUhidPrefix?: string;
    enableBEGlobalSearch?: boolean;
}

/**
 * @todo move out of session
 */
const padSession = {
    isPreviewDirty: false,
    selectedPatient: undefined,
    privacyDialogVisible: false,
    googleReviewDialogVisible: undefined,
    showEkaCommunications: false,
    refreshPage: false,
    isGlobalSearchVisible: false,
    isStorageModalVisible: false,
    iframeDialogUrl: undefined,
    editPatient: undefined,
    ptMedicalHistory: undefined,
    storageInfo: undefined,
    currentMonthLoss: undefined,
    careCanvasPreview: undefined,
    showLogoutModal: false,
};

export const initialSessionState: SessionReducerV1 = {
    ...padSession,
    isNotesMode: undefined,
    dontAllowStartVisit: undefined,
    isSettingUp: true,
    ...DEFAULT_GENIE_SETTINGS,
    isTreatmentPlanEnabled: false,
    isDicomEnabled: false,
    businessUhidPrefix: undefined,
    enableBEGlobalSearch: false,
};

const sessionReducer = (state = initialSessionState, action: Actions): SessionReducerV1 => {
    switch (action.type) {
        case SETUP_SETTINGS:
            return {
                ...state,
                ...action.settings,
                medElements: getMedElementsMergedWithDefault(action.settings.medElements),
                isSettingUp: false,
            };
        case UPSERT_GENIE_SETTINGS:
            return {
                ...state,
                ...action.setting,
            };
        case LOAD_VITALS_CONFIG:
        case UPSERT_VITALS_CONFIG:
            return {
                ...state,
                padConfig: {
                    ...state.padConfig,
                    vitalsPadConfig: action.vitalsConfig,
                },
            };

        case LOAD_CALCULATOR_CONFIG:
        case UPSERT_CALCULATOR_CONFIG:
            return {
                ...state,
                padConfig: {
                    ...state.padConfig,
                    calculatorPadConfig: action.calculatorConfig,
                },
            };
        case LOAD_SECTION_NAME_CONFIG:
        case UPSERT_SECTION_NAME_CONFIG:
            return {
                ...state,
                padConfig: {
                    ...state.padConfig,
                    sectionNameConfig: action.sectionNameConfig,
                },
            };
        case LOAD_PAD_DY_FORM:
            return {
                ...state,
                padConfig: {
                    ...state.padConfig,
                    dyForm: action.padDyForm,
                },
            };
        case SET_EDIT_PATIENT:
            return {
                ...state,
                editPatient: action.patient,
                ptMedicalHistory: action.ptMedicalHistory,
            };
        case TOGGLE_PRIVACY_DIALOG:
            return {
                ...state,
                privacyDialogVisible: action.isVisible,
            };
        case TOGGLE_GOOGLE_REVIEW_DIALOG:
            return {
                ...state,
                googleReviewDialogVisible: action.isGoogleReviewDialogVisible,
            };
        case TOGGLE_SHOW_EKA_COMM:
            return {
                ...state,
                showEkaCommunications: action.showEkaCommunications,
            };
        case TOGGLE_GLOBAL_SEARCH:
            return {
                ...state,
                isGlobalSearchVisible: action.isGlobalSearchVisible,
            };
        case TOGGLE_STORAGE_MODAL:
            return {
                ...state,
                isStorageModalVisible: action.isStorageModalVisible,
            };
        case TOGGLE_IFRAME_DIALOG:
            return {
                ...state,
                iframeDialogUrl: action.url,
            };
        case REFRESH_ALL_PATIENTS:
            return {
                ...state,
                refreshPage: !state.refreshPage,
            };
        case SET_PREVIEW_DIRTY:
            return {
                ...state,
                isPreviewDirty: action.isPrintPreview,
            };
        case LOAD_PRESCRIPTION: {
            return {
                ...state,
                ...padSession,
                selectedPatient: action.prescription.patient,
            };
        }
        case SET_EXTRA_FLAGS: {
            const { balance, abha_potential_loss, storageInfo, currentMonthLoss, claimableAmount } =
                action?.extraFlags || {};
            return {
                ...state,
                khataCredits: Number(balance),
                abhaPotentialLoss: Number(abha_potential_loss),
                storageInfo: storageInfo,
                currentMonthLoss: currentMonthLoss,
                claimableAmount: claimableAmount,
            };
        }
        case UPDATE_CLAIMABLE_AMOUNT: {
            return {
                ...state,
                claimableAmount: action.claimableAmount,
            };
        }
        case UPDATE_GENIE_PROFILE: {
            // TODO: this should be re-thought
            const { balance, abha_potential_loss, storageInfo, currentMonthLoss, claimableAmount } =
                action.genieProfiles[0]?.extraFlags || {};
            const newState: SessionReducerV1 = {
                ...state,
                ...action.genieProfiles[0].settings,

                medElements: getMedElementsMergedWithDefault(
                    action.genieProfiles[0].settings?.medElements || state.medElements || [],
                ),
                padConfig: {
                    vitalsPadConfig:
                        action.genieProfiles[0]?.vitalsConfig ||
                        state?.padConfig?.vitalsPadConfig ||
                        DEFAULT_VITALS_CONFIG_VALUES,
                    calculatorPadConfig:
                        action.genieProfiles[0]?.calculatorConfig ||
                        state?.padConfig?.calculatorPadConfig ||
                        DEFAULT_CALCULATOR_CONFIG_VALUES,
                    sectionNameConfig:
                        action.genieProfiles[0]?.sectionNameConfig ||
                        state?.padConfig?.sectionNameConfig ||
                        DEFAULT_SECTION_NAME_CONFIG_VALUES,
                    dyForm: action.genieProfiles[0]?.padDForms || state.padConfig?.dyForm || [],
                },
                khataCredits: balance !== undefined ? Number(balance) : state.khataCredits,
                storageInfo: storageInfo !== undefined ? storageInfo : state.storageInfo,
                abhaPotentialLoss:
                    abha_potential_loss !== undefined
                        ? Number(abha_potential_loss)
                        : state.abhaPotentialLoss,
                currentMonthLoss:
                    currentMonthLoss !== undefined
                        ? Number(currentMonthLoss)
                        : state.currentMonthLoss,
                claimableAmount:
                    claimableAmount !== undefined ? claimableAmount : state.claimableAmount,
            };

            if (action.genieProfiles[0].settings?.padElements) {
                const padElements = getPadElementMergedWithDefault(
                    action.genieProfiles[0].settings?.padElements || [],
                    action.genieProfiles[0].padDForms || [],
                );

                newState['padElements'] = padElements;
            }

            return newState;
        }
        case CLEAR_PRESCRIPTION_PAD: {
            return {
                ...state,
                ...padSession,
                isNotesMode: undefined,
                dontAllowStartVisit: undefined,
            };
        }
        case UPDATE_GENIE_SETTINGS: {
            return { ...state, [action.key]: action.value };
        }
        case UPDATE_FOLLOWUP:
        case UPDATE_EXAMINATION:
        case UPDATE_EXAMINATION_FINDINGS:
        case UPSERT_ADVICE:
        case DELETE_ADVICE_PERMANENTLY:
        case EDIT_ADVICE:
        case DELETE_ADVICE:
        case UPDATE_VITALS:
        case UPDATE_VITALS_V0:
        case UPSERT_MEDICAL_HISTORY:
        case DELETE_MEDICAL_HISTORY:
        case CLEAR_MEDICAL_HISTORY:
        case UPDATE_PP_NOTES:
        case UPDATE_PP_PRIVATE_NOTES:
        case UPDATE_REFERAL:
        case UPSERT_DYFORM:
        case UPSERT_LAB_VITAL:
        case DELETE_LAB_VITAL:
        case LOAD_PRESCRIPTION_TEMPLATE:
        case UPDATE_GENIE_SETTINGS:
        case UPSERT_ADMINISTERED_VACCINES:
        case UPSERT_MILESTONES:
        case CLEAR_ADMINISTERED_VACCINES:
        case UPDATE_MILESTONE:
        case SET_MILESTONES:
            return {
                ...state,
                isPreviewDirty: true,
            };
        case DOWNLOAD_PT_DIR: {
            const xlsx = document.createElement('script');
            xlsx.onload = () =>
                createAllPtsExcelAndDownload().then(() =>
                    notification.success({
                        message: 'Patient Directory Downloaded',
                        duration: 5,
                    }),
                );
            xlsx.src = '/xlsx-sheet.js';
            document.getElementsByTagName('head')[0].appendChild(xlsx);
            return state;
        }
        case SET_KHATA_CREDITS: {
            const { khataCredits } = action;
            return {
                ...state,
                khataCredits,
            };
        }
        case SET_PINCODE: {
            const { pincode } = action;
            return {
                ...state,
                pincode,
            };
        }
        case SET_ACTIVE_ORDER_UPDATES_COUNT: {
            const { activeOrderUpdatesCount } = action;
            return {
                ...state,
                activeOrderUpdatesCount,
            };
        }
        case SET_DONT_ALLOW_START_VISIT: {
            const { dontAllowStartVisit } = action;
            return {
                ...state,
                dontAllowStartVisit,
            };
        }
        case SET_IS_NOTES_MODE: {
            const { isNotesMode } = action;
            return {
                ...state,
                isNotesMode,
            };
        }
        case SET_IS_TREATMENT_PLAN_ENABLED: {
            const { isTreatmentPlanEnabled } = action;
            return {
                ...state,
                isTreatmentPlanEnabled: isTreatmentPlanEnabled,
            };
        }
        case SET_IS_DICOM_ENABLED: {
            const { isDicomEnabled } = action;
            return {
                ...state,
                isDicomEnabled: isDicomEnabled,
            };
        }
        case SET_CARE_CANVAS_PREVIEW:
            return {
                ...state,
                careCanvasPreview: action.careCanvasPreview,
            };
        case TOGGLE_LOGOUT_MODAL:
            return {
                ...state,
                showLogoutModal: action.showLogoutModal,
            };
        case SET_BUSINESS_UHID_PREFIX:
            return {
                ...state,
                businessUhidPrefix: action.prefix,
            };
        case SET_GLOBAL_SEARCH_FLAG:
            return {
                ...state,
                enableBEGlobalSearch: action.enableBEGlobalSearch,
            };
        default:
            return state;
    }
};

export default sessionReducer;
